<template>
  <div class="inventory-session stack tight">
    <UiPanel>
      <div class="header">
        <div>
          <h3 class="title">Inventory</h3>
          <div class="subtitle text-muted">Items, carry, and quick actions.</div>
        </div>

        <div class="header-controls">
          <UiChip v-if="carry" :variant="carry.encumbered ? 'danger' : 'muted'" :title="carryTitle">
            <span class="chip-icon" aria-hidden="true">{{ ICONS.sessions.inventory }}</span>
            <span class="stat-mono num">{{ formatNumber(carry.slotsUsed) }}</span>
            <span class="text-muted">/</span>
            <span class="stat-mono num">{{ formatNumber(carry.carryCapacitySlots) }}</span>
            <span v-if="carry.heavyEncumbered" class="chip-note">Heavy</span>
            <span v-else-if="carry.encumbered" class="chip-note">Encumbered</span>
          </UiChip>

          <UiButton
            variant="secondary"
            size="sm"
            :disabled="sessionStore.loading || gold <= 0"
            @click="openGoldDrop"
            title="Drop gold"
          >
            <template #icon>{{ ICONS.itemsByType.currency }}</template>
            <span class="stat-mono num">{{ formatNumber(gold) }}</span>
          </UiButton>
        </div>
      </div>

      <div class="controls">
        <UiTabs v-model="filter" :tabs="filterTabs" />
        <div class="controls-right">
          <label class="search">
            <span class="sr-only">Search inventory</span>
            <input v-model="searchQuery" class="ui-input" placeholder="Search…" />
          </label>
          <label class="select-label">
            <span class="sr-only">Item mark mode</span>
            <select v-model="itemMarkMode" class="ui-select">
              <option value="sigil">Sigil</option>
              <option value="shortcode">Shortcode</option>
            </select>
          </label>
        </div>
      </div>
    </UiPanel>

    <UiCallout v-if="filteredItems.length === 0" variant="info">
      <template v-if="searchQuery.trim()">No items match “{{ searchQuery.trim() }}”.</template>
      <template v-else>No items right now.</template>
    </UiCallout>

    <div v-else class="grid" ref="gridRef">
      <div v-for="entry in filteredItems" :key="getEntryKey(entry)" class="tile-wrap">
        <UiTooltip :hoverDelay="360" fullWidth>
          <button
            class="tile"
            :class="[`rarity-${entry.item?.rarity || 'common'}`, { selected: selectedItemId === getEntryKey(entry) }]"
            type="button"
            @click="handleItemClick(entry)"
          >
            <div
              v-if="entry.enchantment"
              class="enchant-flag"
              role="img"
              :aria-label="`Enchanted: ${formatEnchantment(entry.enchantment)}`"
            >
              {{ ICONS.skills.enchanting }}
            </div>
            <div class="tile-top">
              <div class="icon">
                <span class="icon-type">{{ getItemIcon(entry.item?.type) }}</span>
                <span class="icon-mark" aria-hidden="true">{{ markForEntry(entry) }}</span>
              </div>
              <div class="qty stat-mono num" aria-label="Quantity">{{ formatNumber(entry.quantity) }}</div>
            </div>

            <div class="name" :title="entry.item?.name || entry.itemId">
              {{ entry.item?.name || entry.itemId }}
            </div>
            <div class="meta text-muted">
              <span>{{ entry.item?.type || "Unknown" }}</span>
              <span class="meta-dot">•</span>
              <span class="stat-mono num">{{ formatNumber(entry.referencePrice?.price ?? entry.item?.basePrice ?? 0) }}g</span>
            </div>
          </button>

	          <template #content>
	            <div class="tooltip stack tight">
	              <div class="tooltip-title">
	                <span class="tooltip-icon">{{ getItemIcon(entry.item?.type) }}</span>
	                <div style="flex: 1; min-width: 0;">
	                  <div class="tooltip-name">{{ entry.item?.name || entry.itemId }}</div>
	                  <div class="text-muted tooltip-sub">
	                    {{ tooltipMarkForEntry(entry) }} • {{ entry.item?.rarity || "common" }} • {{ entry.item?.type || "Unknown" }}
	                  </div>
	                </div>
	              </div>

              <div class="tooltip-kv">
                <div>
                  <div class="kv-label">Quantity</div>
                  <div class="kv-value stat-mono num">×{{ formatNumber(entry.quantity) }}</div>
                </div>
                <div>
                  <div class="kv-label">Value</div>
                  <div class="kv-value stat-mono num">
                    {{ formatNumber(entry.referencePrice?.price ?? entry.item?.basePrice ?? 0) }}g
                    <span v-if="entry.referencePrice" class="text-muted">({{ entry.referencePrice.source }})</span>
                  </div>
                </div>
              </div>

              <div v-if="entry.enchantment" class="text-muted">
                {{ ICONS.skills.enchanting }} {{ formatEnchantment(entry.enchantment) }}
              </div>
              <div v-for="line in getArmorIdentityLines(entry.item)" :key="line" class="text-muted">
                {{ line }}
              </div>
              <div v-if="entry.item?.description" class="text-muted">
                {{ entry.item.description }}
              </div>

              <div class="tooltip-actions">
                <UiButton
                  v-if="entry.item?.equipmentSlot"
                  size="sm"
                  variant="secondary"
                  :disabled="sessionStore.loading"
                  @click.stop="handleEquip(entry)"
                >
                  Equip
                </UiButton>
                <UiButton
                  v-else-if="isConsumable(entry)"
                  size="sm"
                  variant="secondary"
                  :disabled="sessionStore.loading || usingItemId === entry.itemId || canUseConsumables === false"
                  :title="canUseConsumables === false && useDisabledReason ? useDisabledReason : 'Use item'"
                  @click.stop="emit('use', entry.itemId)"
                >
                  Use
                </UiButton>
                <UiButton
                  v-else-if="entry.item?.type === 'container'"
                  size="sm"
                  variant="secondary"
                  :disabled="sessionStore.loading"
                  @click.stop="emit('open-containers')"
                >
                  Open
                </UiButton>
                <UiButton size="sm" variant="danger" :disabled="sessionStore.loading" @click.stop="openItemDrop(entry)">
                  Drop
                </UiButton>
              </div>
            </div>
          </template>
        </UiTooltip>

        <div v-if="selectedItemId === getEntryKey(entry)" class="actions">
          <UiButton
            v-if="entry.item?.type === 'container'"
            size="sm"
            variant="secondary"
            :disabled="sessionStore.loading"
            @click.stop="emit('open-containers')"
          >
            <template #icon>{{ ICONS.itemsByType.container }}</template>
            Open
          </UiButton>

          <UiButton
            v-if="isConsumable(entry)"
            size="sm"
            variant="secondary"
            :disabled="sessionStore.loading || usingItemId === entry.itemId || canUseConsumables === false"
            :title="canUseConsumables === false && useDisabledReason ? useDisabledReason : 'Use item'"
            @click.stop="emit('use', entry.itemId)"
          >
            <template #icon>{{ ICONS.itemsByType.consumable }}</template>
            Use
          </UiButton>

          <UiButton
            v-if="entry.item?.equipmentSlot"
            size="sm"
            variant="secondary"
            :disabled="sessionStore.loading"
            @click.stop="handleEquip(entry)"
          >
            <template #icon>{{ ICONS.sessions.equipment }}</template>
            Equip
          </UiButton>

          <UiButton size="sm" variant="danger" :disabled="sessionStore.loading" @click.stop="openItemDrop(entry)">
            <template #icon>{{ ICONS.actions.trash }}</template>
            Drop
          </UiButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import type { CarryState, Enchantment, InventoryEntry, Item } from "@idle-rpg/shared";
import { useSessionStore } from "../../../store/session";
import { useToast } from "../../../composables/useToast";
import { ICONS } from "../../../lib/icons";
import { itemIconForType } from "../../../lib/itemIcons";
import { useItemMarkMode } from "../../../composables/useItemMarkMode";
import { getItemMarkDisplay, getItemMarkTooltipDisplay, resolveItemMarkDisplays } from "../../../lib/itemMarks";
import { formatNumber } from "../../../lib/format";
import { useQuantityPrompt } from "../../../composables/useQuantityPrompt";
import { compareEntriesByStackedValue } from "../../../lib/inventorySort";
import UiButton from "../../../components/ui/UiButton.vue";
import UiCallout from "../../../components/ui/UiCallout.vue";
import UiChip from "../../../components/ui/UiChip.vue";
import UiPanel from "../../../components/ui/UiPanel.vue";
import UiTabs from "../../../components/ui/UiTabs.vue";
import UiTooltip from "../../../components/ui/UiTooltip.vue";

const props = defineProps<{
  items: InventoryEntry[];
  gold: number;
  carry?: CarryState | null;
  canUseConsumables?: boolean;
  useDisabledReason?: string | null;
  usingItemId?: string | null;
}>();

const emit = defineEmits<{
  drop: [itemId: string, qty: number];
  use: [itemId: string];
  "open-containers": [];
}>();

const sessionStore = useSessionStore();
const { addToast } = useToast();
const { promptQuantity } = useQuantityPrompt();
const { mode: itemMarkMode } = useItemMarkMode();
const selectedItemId = ref<string | null>(null);
const searchQuery = ref("");
const gridRef = ref<HTMLElement | null>(null);

const NAME_MAX_FONT_REM = 0.95;
const NAME_MIN_FONT_REM = 0.66;
const NAME_MIN_SCALE = NAME_MIN_FONT_REM / NAME_MAX_FONT_REM;
const NAME_SOFT_START = 16;
const NAME_SOFT_STEP = 0.02;
let nameFitFrame = 0;
let nameFitObserver: ResizeObserver | null = null;

const carry = computed(() => props.carry || null);
const carryTitle = computed(() => {
  if (!carry.value) return "";
  if (carry.value.heavyEncumbered) return "Heavy encumbrance";
  if (carry.value.encumbered) return "Encumbered";
  return "Unburdened";
});

function getEntryKey(entry: InventoryEntry): string {
  return `${entry.itemId}-${entry.variantKey || "base"}-${entry.enchantment?.id || "base"}-${entry.instanceIds?.[0] || "stack"}`;
}

function handleItemClick(entry: InventoryEntry) {
  const key = getEntryKey(entry);
  selectedItemId.value = selectedItemId.value === key ? null : key;
}

async function handleEquip(entry: InventoryEntry) {
  if (!entry.item?.equipmentSlot) return;
  const instanceId = entry.instanceIds?.[0];
  const ok = await sessionStore.equipItem(entry.itemId, entry.item.equipmentSlot, instanceId);
  if (!ok) addToast(sessionStore.error || "Unable to equip item", "warning");
  selectedItemId.value = null;
}

function openItemDrop(entry: InventoryEntry) {
  const name = entry.item?.name || entry.itemId;
  promptDrop({
    itemId: entry.itemId,
    itemName: name,
    icon: itemIconForType(entry.item?.type),
    maxQty: Math.max(0, entry.quantity)
  });
}

function openGoldDrop() {
  promptDrop({
    itemId: "item-gold",
    itemName: "Gold",
    icon: ICONS.itemsByType.currency,
    maxQty: Math.max(0, props.gold)
  });
}

async function promptDrop(options: { itemId: string; itemName: string; icon: string; maxQty: number }) {
  if (sessionStore.loading) return;
  if (options.maxQty <= 0) return;
  const qty = await promptQuantity({
    title: "Drop quantity",
    subtitle: options.itemName,
    icon: options.icon,
    min: 1,
    max: options.maxQty,
    initial: Math.min(1, options.maxQty),
    confirmLabel: "Drop"
  });
  if (!qty) return;
  emit("drop", options.itemId, qty);
  selectedItemId.value = null;
}

type FilterType = "all" | "equipment" | "resources" | "consumables";
const filter = ref<FilterType>("all");

const filterTabs: { value: FilterType; label: string; icon: string }[] = [
  { value: "all", label: "All", icon: ICONS.sessions.inventory },
  { value: "equipment", label: "Gear", icon: ICONS.sessions.equipment },
  { value: "resources", label: "Mats", icon: ICONS.itemsByType.resource },
  { value: "consumables", label: "Use", icon: ICONS.itemsByType.consumable }
];

const filteredItems = computed(() => {
  const filtered = props.items
    .filter((entry) => {
      const type = entry.item?.type;
      if (filter.value === "all" || !type) return true;

      const equipmentTypes = ["weapon", "armor", "tool", "ammo", "jewelry"];
      const resourceTypes = ["resource", "gem", "bait", "currency"];
      const consumableTypes = ["consumable", "spell-scroll"];

      if (filter.value === "equipment") return equipmentTypes.includes(type);
      if (filter.value === "resources") return resourceTypes.includes(type);
      if (filter.value === "consumables") return consumableTypes.includes(type);
      return true;
    })
    .filter((entry) => {
      const query = searchQuery.value.trim().toLowerCase();
      if (!query) return true;
      const name = (entry.item?.name || entry.itemId || "").toLowerCase();
      const type = (entry.item?.type || "").toLowerCase();
      return name.includes(query) || type.includes(query);
    });

  return filtered.sort(compareEntriesByStackedValue);
});

const visibleMarkByKey = computed(() =>
  resolveItemMarkDisplays(
    itemMarkMode.value,
    filteredItems.value.map((entry) => ({
      key: getEntryKey(entry),
      itemId: entry.itemId,
      variantKey: entry.variantKey ?? null,
      enchantmentId: entry.enchantment?.id ?? null
    }))
  )
);

function getItemIcon(type?: string): string {
  return itemIconForType(type);
}

function formatEnchantment(enchantment: Enchantment): string {
  if (enchantment.effectType === "attribute") {
    const target = (enchantment.target || "attr").toString().toUpperCase();
    return `${enchantment.name} (+${enchantment.amount} ${target})`;
  }
  if (enchantment.effectType === "skill") {
    const target = (enchantment.target || "skill").toString();
    const label = target.charAt(0).toUpperCase() + target.slice(1);
    return `${enchantment.name} (+${enchantment.amount} ${label})`;
  }
  if (enchantment.effectType === "ward") {
    const pool = (enchantment.wardType || "hp").toUpperCase();
    return `${enchantment.name} (${enchantment.amount} ${pool} Ward)`;
  }
  return enchantment.name;
}

function getArmorIdentityLines(item?: Item): string[] {
  if (!item?.armorCategory) return [];
  const lines: string[] = [];
  const category = item.armorCategory ? item.armorCategory[0].toUpperCase() + item.armorCategory.slice(1) : "Armor";
  const tier = item.armorTier ? `Tier ${item.armorTier}` : "Tier ?";
  lines.push(`Category: ${category} • ${tier}`);
  if (item.carryCapacitySlotsMod && item.carryCapacitySlotsMod !== 0) {
    const sign = item.carryCapacitySlotsMod > 0 ? "+" : "";
    lines.push(`Carry Capacity ${sign}${item.carryCapacitySlotsMod}`);
  }
  if (item.maxManaMod && item.maxManaMod !== 0) {
    const sign = item.maxManaMod > 0 ? "+" : "";
    lines.push(`Max Mana ${sign}${item.maxManaMod}`);
  }
  if (item.manaRegenBonusPerTick && item.manaRegenBonusPerTick !== 0) {
    const sign = item.manaRegenBonusPerTick > 0 ? "+" : "";
    lines.push(`Mana Regen ${sign}${item.manaRegenBonusPerTick}/tick`);
  }
  return lines;
}

function markForEntry(entry: InventoryEntry): string {
  return (
    visibleMarkByKey.value.get(getEntryKey(entry)) ??
    getItemMarkDisplay(itemMarkMode.value, entry.itemId, entry.variantKey ?? null, entry.enchantment?.id ?? null)
  );
}

function tooltipMarkForEntry(entry: InventoryEntry): string {
  return getItemMarkTooltipDisplay(itemMarkMode.value, entry.itemId, entry.variantKey ?? null, entry.enchantment?.id ?? null);
}

function isConsumable(entry: InventoryEntry): boolean {
  if (entry.item?.type === "consumable") return true;
  if (entry.item?.type === "spell-scroll") return Boolean((entry.item as any).scrollSpellId);
  return false;
}

function fitNameText(el: HTMLElement) {
  const availableWidth = el.clientWidth;
  if (!availableWidth) return;
  el.style.setProperty("--name-scale", "1");
  const fullWidth = el.scrollWidth;
  if (!fullWidth) return;
  const nameLength = (el.textContent || "").trim().length;
  const softScale =
    nameLength > NAME_SOFT_START
      ? Math.max(NAME_MIN_SCALE, 1 - (nameLength - NAME_SOFT_START) * NAME_SOFT_STEP)
      : 1;
  const fitScale = Math.min(1, availableWidth / fullWidth);
  const clampedScale = Math.max(NAME_MIN_SCALE, Math.min(fitScale, softScale));
  el.style.setProperty("--name-scale", clampedScale.toFixed(3));
}

function fitAllNames() {
  const grid = gridRef.value;
  if (!grid) return;
  grid.querySelectorAll<HTMLElement>(".name").forEach((node) => fitNameText(node));
}

function scheduleNameFit() {
  if (nameFitFrame) cancelAnimationFrame(nameFitFrame);
  nameFitFrame = requestAnimationFrame(() => {
    nameFitFrame = 0;
    fitAllNames();
  });
}

watch(
  gridRef,
  (grid) => {
    if (nameFitObserver) {
      nameFitObserver.disconnect();
      nameFitObserver = null;
    }
    if (!grid || typeof ResizeObserver === "undefined") return;
    nameFitObserver = new ResizeObserver(scheduleNameFit);
    nameFitObserver.observe(grid);
    scheduleNameFit();
  },
  { flush: "post" }
);

watch(filteredItems, async () => {
  await nextTick();
  scheduleNameFit();
});

onMounted(() => {
  if (typeof window === "undefined") return;
  window.addEventListener("resize", scheduleNameFit);
});

onBeforeUnmount(() => {
  if (nameFitObserver) nameFitObserver.disconnect();
  if (nameFitFrame) cancelAnimationFrame(nameFitFrame);
  if (typeof window === "undefined") return;
  window.removeEventListener("resize", scheduleNameFit);
});
</script>

<style scoped>
.inventory-session {
  min-width: 0;
}

.header {
  display: flex;
  justify-content: space-between;
  gap: var(--space-md);
  align-items: flex-start;
}

.title {
  margin: 0;
  font-family: var(--font-display);
  letter-spacing: 0.02em;
}

.subtitle {
  font-size: 0.9rem;
  margin-top: 0.15rem;
}

.header-controls {
  display: flex;
  gap: var(--space-sm);
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.chip-icon {
  margin-right: 0.25rem;
}

.chip-note {
  margin-left: 0.35rem;
  font-size: 0.75rem;
  opacity: 0.8;
}

.controls {
  margin-top: var(--space-md);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-md);
  flex-wrap: wrap;
}

.controls-right {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
}

.ui-select {
  padding: 0.45rem 2rem 0.45rem 0.65rem;
  font-size: 0.9rem;
}

.ui-input {
  padding: 0.45rem 0.65rem;
  font-size: 0.9rem;
  width: min(260px, 60vw);
}

.search {
  display: inline-flex;
  align-items: center;
}

.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: var(--space-md);
  align-items: start;
}

.tile-wrap {
  display: grid;
  gap: var(--space-sm);
  min-width: 0;
}

.tile {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  text-align: left;
  padding: var(--space-md);
  border-radius: var(--radius-md);
  background: var(--glass-0);
  border: 1px solid var(--glass-border-0);
  box-shadow: var(--shadow-0);
  transition: transform var(--dur-1) var(--ease-out), border-color var(--dur-1) var(--ease-out), box-shadow var(--dur-1) var(--ease-out);
  position: relative;
  --tile-accent: var(--state-idle);
}

.tile:hover {
  transform: translateY(-1px);
  border-color: color-mix(in srgb, var(--accent) 42%, var(--glass-border-0));
}

.tile.selected {
  border-color: color-mix(in srgb, var(--accent) 65%, var(--glass-border-0));
  box-shadow: var(--shadow-accent);
}

.tile:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent) 18%, transparent), var(--shadow-0);
}

.tile::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  opacity: 0.7;
  background: linear-gradient(90deg, color-mix(in srgb, var(--tile-accent) 24%, transparent), transparent 55%);
}

.enchant-flag {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  font-size: 0.75rem;
  color: var(--fg-0);
  background: color-mix(in srgb, var(--accent-social) 55%, transparent);
  border: 1px solid color-mix(in srgb, var(--accent-social) 70%, transparent);
  z-index: 2;
}

.tile.rarity-common { --tile-accent: var(--state-idle); }
.tile.rarity-uncommon { --tile-accent: var(--accent-work); }
.tile.rarity-rare { --tile-accent: var(--accent-travel); }
.tile.rarity-epic { --tile-accent: var(--accent-social); }

.tile-top {
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-sm);
}

.icon {
  display: flex;
  align-items: baseline;
  gap: 0.35rem;
}

.icon-type {
  font-size: 1.4rem;
}

.icon-mark {
  font-family: var(--font-mono);
  font-size: 0.85rem;
  color: color-mix(in srgb, var(--fg-0) 86%, transparent);
}

.qty {
  font-family: var(--font-mono);
  font-size: 0.9rem;
  color: var(--color-muted);
}

.name {
  margin-top: 0.55rem;
  font-weight: 650;
  position: relative;
  min-width: 0;
  max-width: 100%;
  line-height: 1.1;
  font-size: clamp(0.66rem, calc(0.95rem * var(--name-scale, 1)), 0.95rem);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.meta {
  margin-top: 0.3rem;
  font-size: 0.8rem;
  position: relative;
}

.meta-dot {
  margin: 0 0.3rem;
  opacity: 0.65;
}

.actions {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(90px, 1fr));
  gap: var(--space-sm);
}

.tooltip-title {
  display: flex;
  gap: 0.6rem;
  align-items: flex-start;
}

.tooltip-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.tooltip-icon {
  font-size: 1.4rem;
  line-height: 1;
}

.tooltip-name {
  font-weight: 700;
  font-family: var(--font-display);
  letter-spacing: 0.02em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tooltip-sub {
  font-family: var(--font-mono);
  font-size: 0.8rem;
}

.tooltip-kv {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-md);
}

.kv-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--color-subtle);
}

.kv-value {
  margin-top: 0.1rem;
  font-weight: 650;
}

@media (max-width: 780px) {
  .grid {
    grid-template-columns: repeat(auto-fill, minmax(145px, 1fr));
  }
}
</style>
